import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Gift, Copy, Users, ArrowRight, CheckCircle, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API}/referral/signup`, { email: email.trim(), name: name.trim() });
      // Now fetch the full dashboard
      const dash = await axios.get(`${API}/referral/dashboard/${encodeURIComponent(email.trim())}`);
      setDashboard(dash.data);
      setLookupEmail(email.trim());
      toast.success('Welcome to the referral program!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupEmail.trim()) return;
    setLookupLoading(true);

    try {
      const res = await axios.get(`${API}/referral/dashboard/${encodeURIComponent(lookupEmail.trim())}`);
      setDashboard(res.data);
    } catch {
      toast.error('Not found. Sign up for the referral program first.');
    } finally {
      setLookupLoading(false);
    }
  };

  const copyLink = () => {
    if (dashboard?.referral_link) {
      navigator.clipboard.writeText(dashboard.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Discount code copied!');
  };

  const shareWhatsApp = () => {
    const text = `Hey! I love The Perfect Peach Club products. Use my referral link and get R30 off your combo purchase: ${dashboard?.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Refer a Friend | The Perfect Peach Club</title>
        <meta name="description" content="Share the love! Refer a friend to The Perfect Peach Club and you both get R30 off your next combo purchase." />
      </Helmet>
      <Navigation />
      <ChatWidget />

      <div className="max-w-3xl mx-auto px-5 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-perfect-peach/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-perfect-peach" />
          </div>
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2420] mb-3" data-testid="referral-heading">
            Share The Love
          </h1>
          <p className="text-african-earth text-base md:text-lg max-w-lg mx-auto">
            Refer a friend and you <strong>both</strong> get <strong>R30 off</strong> your next combo purchase
          </p>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft mb-8">
          <h2 className="font-playfair text-xl font-semibold text-[#2D2420] mb-5 text-center">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Share your link', desc: 'Send your unique referral link to a friend' },
              { step: '2', title: 'Friend buys a combo', desc: 'They get R30 off their combo purchase' },
              { step: '3', title: 'You get rewarded', desc: "You receive a R30 discount code via email" },
            ].map(s => (
              <div key={s.step} className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-perfect-peach text-white flex items-center justify-center mx-auto mb-3 text-sm font-bold">{s.step}</div>
                <p className="font-medium text-[#2D2420] text-sm mb-1">{s.title}</p>
                <p className="text-xs text-african-earth">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {!dashboard ? (
          <>
            {/* Sign Up Form */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft mb-6">
              <h2 className="font-playfair text-xl font-semibold text-[#2D2420] mb-4">Join the Program</h2>
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-african-earth font-medium text-sm mb-1.5">Your Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="referral-name-input" />
                </div>
                <div>
                  <label className="block text-african-earth font-medium text-sm mb-1.5">Your Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com" className="w-full border border-[#E0D5D0] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="referral-email-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-perfect-peach text-white py-3.5 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="referral-signup-button">
                  {loading ? 'Joining...' : <><Gift className="w-5 h-5" /> Get My Referral Link</>}
                </button>
              </form>
            </div>

            {/* Already a member */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft">
              <h2 className="font-playfair text-lg font-semibold text-[#2D2420] mb-3">Already a member?</h2>
              <form onSubmit={handleLookup} className="flex gap-3">
                <input type="email" value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} required placeholder="Enter your email" className="flex-1 border border-[#E0D5D0] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-perfect-peach" data-testid="referral-lookup-email" />
                <button type="submit" disabled={lookupLoading} className="bg-[#2D2420] text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-[#3d342e] transition-all disabled:opacity-50" data-testid="referral-lookup-button">
                  {lookupLoading ? '...' : 'View'}
                </button>
              </form>
            </div>
          </>
        ) : (
          /* Dashboard */
          <div className="space-y-6" data-testid="referral-dashboard">
            {/* Referral Link Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft">
              <h2 className="font-playfair text-xl font-semibold text-[#2D2420] mb-4">Your Referral Link</h2>
              <div className="flex gap-2 mb-4">
                <div className="flex-1 bg-bone-white rounded-xl px-4 py-3 text-sm text-african-earth truncate font-mono" data-testid="referral-link-display">
                  {dashboard.referral_link}
                </div>
                <button onClick={copyLink} className="bg-perfect-peach text-white px-4 py-3 rounded-xl hover:bg-[#C57D63] transition-colors flex-shrink-0" data-testid="copy-referral-link">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-3">
                <button onClick={shareWhatsApp} className="flex-1 bg-[#25D366] text-white py-2.5 rounded-xl font-medium text-sm hover:bg-[#1ea952] transition-colors flex items-center justify-center gap-2" data-testid="share-whatsapp-referral">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Share via WhatsApp
                </button>
                <button onClick={copyLink} className="flex-1 bg-[#2D2420] text-white py-2.5 rounded-xl font-medium text-sm hover:bg-[#3d342e] transition-colors flex items-center justify-center gap-2">
                  <Copy className="w-4 h-4" /> Copy Link
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
                <Users className="w-6 h-6 text-perfect-peach mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#2D2420]" data-testid="rewards-earned">{dashboard.rewards_earned}</p>
                <p className="text-xs text-african-earth">Friends referred</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-soft text-center">
                <Gift className="w-6 h-6 text-perfect-peach mx-auto mb-2" />
                <p className="text-2xl font-bold text-[#2D2420]" data-testid="rewards-remaining">{dashboard.rewards_remaining}</p>
                <p className="text-xs text-african-earth">Referrals left</p>
              </div>
            </div>

            {/* Available Reward Codes */}
            {dashboard.available_reward_codes?.length > 0 && (
              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft">
                <h2 className="font-playfair text-xl font-semibold text-[#2D2420] mb-4">Your Reward Codes</h2>
                <div className="space-y-3">
                  {dashboard.available_reward_codes.map((reward) => (
                    <div key={reward.code} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-4" data-testid={`reward-code-${reward.code}`}>
                      <div>
                        <span className="font-mono font-bold text-green-700 text-lg">{reward.code}</span>
                        <p className="text-xs text-green-600 mt-0.5">R{reward.amount} off combo purchase</p>
                      </div>
                      <button onClick={() => copyCode(reward.code)} className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-1">
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fine print */}
            <p className="text-center text-african-earth/50 text-xs">
              Max {10} referrals. R30 discount applies to combo purchases only. Codes are one-time use.
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ReferralPage;
